import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Eye, Edit3, Plus, MapPin, Heart,
  ShoppingBag, User, Mail, Phone, Lock,
  ArrowRight, Check, Trash2,
} from "lucide-react";
import Auth from "../../utils/AuthUtils";
import userAPI from "../../api/UserApi";
import { getTimeAgo } from "../../helper/Helper";

gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────
   STATUS MAP
───────────────────────────────────────────── */
const STATUS_MAP = {
  Delivered:    { dot: "#2e7d32", color: "#2e7d32" },
  "In Transit": { dot: "#e65100", color: "#e65100" },
  Processing:   { dot: "#1565c0", color: "#1565c0" },
  Pending:      { dot: "#f9a825", color: "#f9a825" },
  Cancelled:    { dot: "#c62828", color: "#c62828" },
};

const TABS = [
  { key: "orders",    label: "Orders",         Icon: ShoppingBag },
  { key: "wishlist",  label: "Wishlist",        Icon: Heart       },
  { key: "addresses", label: "Addresses",       Icon: MapPin      },
  { key: "account",   label: "Account Details", Icon: User        },
];

/* ─────────────────────────────────────────────
   PRODUCT CARD
───────────────────────────────────────────── */
const ProductCard = ({ product }) => {
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className="vpc"
      onMouseEnter={() => gsap.to(ref.current, { y: -7, duration: 0.3, ease: "power2.out" })}
      onMouseLeave={() => gsap.to(ref.current, { y: 0,  duration: 0.3, ease: "power2.out" })}
    >
      <div className="vpc-img-wrap">
        <img src={product.image} alt={product.title} className="vpc-img" />
        {product.badge && <span className="vpc-badge">{product.badge}</span>}
        <div className="vpc-overlay">
          <button className="vpc-quick">Quick View</button>
        </div>
      </div>
      <div className="vpc-body">
        <p className="vpc-cat">{product.category}</p>
        <h4 className="vpc-title">{product.title}</h4>
        <p className="vpc-price">{product.price}</p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SKELETON LOADERS
───────────────────────────────────────────── */
const OrderSkeleton = () => (
  <div className="d-flex flex-column gap-3">
    {[1, 2, 3].map(i => (
      <div key={i} className="vp-order" style={{ opacity: 0.6 }}>
        <div style={{ width: 80, height: 90, background: "var(--border-light)", borderRadius: 4, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="vp-skel wide" style={{ marginBottom: 8 }} />
          <div className="vp-skel medium" style={{ marginBottom: 8 }} />
          <div className="vp-skel short" />
        </div>
      </div>
    ))}
  </div>
);

const WishlistSkeleton = () => (
  <div className="row g-4">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="col-6 col-md-3">
        <div style={{ background: "var(--border-light)", height: 280, borderRadius: 4 }} />
        <div className="vp-skel wide" style={{ marginTop: 10 }} />
        <div className="vp-skel medium" style={{ marginTop: 6 }} />
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function Dashboard() {
  const navigate   = useNavigate();
  const { username } = Auth.getToken();

  // ── Tab state ──
  const [tab, setTab]     = useState("orders");
  const [saved, setSaved] = useState(false);

  // ── API data ──
  const [latestOrder,  setLatestOrder]  = useState(null);   // Dashboard latest order
  const [orders,       setOrders]       = useState([]);
  const [wishlist,     setWishlist]     = useState([]);
  const [addresses,    setAddresses]    = useState([]);
  const [userInfo,     setUserInfo]     = useState({ name: username || "", email: "", phone: "" });
  const [form,         setForm]         = useState({ name: "", email: "", phone: "", curPwd: "", newPwd: "", cfmPwd: "" });

  // ── Loading states per tab ──
  const [loadingOrders,    setLoadingOrders]    = useState(false);
  const [loadingWishlist,  setLoadingWishlist]  = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingAccount,   setLoadingAccount]   = useState(false);

  // ── Refs for GSAP ──
  const heroRef    = useRef(null);
  const tabsRef    = useRef(null);
  const contentRef = useRef(null);
  const inkRef     = useRef(null);
  const tabEls     = useRef([]);

  /* ── Initial: fetch latest order (Dashboard logic) ── */
  useEffect(() => {
    fetchLatestOrder();
  }, []);

  const fetchLatestOrder = async () => {
    try {
      const res = await userAPI.fetchLatestOrder();
      if (res.code === 200) {
        setLatestOrder(res.data);
      }
    } catch (err) {}
  };

  /* ── Fetch data when tab changes ── */
  useEffect(() => {
    if (tab === "orders")    fetchOrders();
    if (tab === "wishlist")  fetchWishlist();
    if (tab === "addresses") fetchAddresses();
    if (tab === "account")   fetchAccount();
  }, [tab]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await userAPI.fetchOrders();
      if (res.code === 200) setOrders(res.data || []);
    } catch (err) {
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchWishlist = async () => {
    setLoadingWishlist(true);
    try {
      const res = await userAPI.fetchWishlist();
      if (res.code === 200) setWishlist(res.data || []);
    } catch (err) {
    } finally {
      setLoadingWishlist(false);
    }
  };

  const fetchAddresses = async () => {
    setLoadingAddresses(true);
    try {
      const res = await userAPI.fetchAddresses();
      if (res.code === 200) setAddresses(res.data || []);
    } catch (err) {
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchAccount = async () => {
    setLoadingAccount(true);
    try {
      const res = await userAPI.fetchProfile();
      if (res.code === 200) {
        const u = res.data;
        setUserInfo(u);
        setForm(f => ({ ...f, name: u.name || "", email: u.email || "", phone: u.phone || "" }));
      }
    } catch (err) {
    } finally {
      setLoadingAccount(false);
    }
  };

  const handleSave = async () => {
    try {
      const res = await userAPI.updateProfile({ name: form.name, email: form.email, phone: form.phone });
      if (res.code === 200) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2600);
      }
    } catch (err) {}
  };

  const handlePasswordUpdate = async () => {
    try {
      await userAPI.changePassword({ currentPassword: form.curPwd, newPassword: form.newPwd, confirmPassword: form.cfmPwd });
    } catch (err) {}
  };

  const handleDeleteAddress = async (id) => {
    try {
      const res = await userAPI.deleteAddress(id);
      if (res.code === 200) setAddresses(prev => prev.filter(a => a.id !== id));
    } catch (err) {}
  };

  const handleSetDefaultAddress = async (id) => {
    try {
      await userAPI.setDefaultAddress(id);
      setAddresses(prev => prev.map(a => ({ ...a, default: a.id === id })));
    } catch (err) {}
  };

  /* ── GSAP entrance ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.timeline({ defaults: { ease: "power3.out" } })
        .fromTo(heroRef.current,    { opacity: 0, y: -28 }, { opacity: 1, y: 0, duration: 0.6  })
        .fromTo(tabsRef.current,    { opacity: 0, y: 14  }, { opacity: 1, y: 0, duration: 0.42 }, "-=0.28")
        .fromTo(contentRef.current, { opacity: 0, y: 20  }, { opacity: 1, y: 0, duration: 0.45 }, "-=0.22");
    });
    return () => ctx.revert();
  }, []);

  /* ── Ink underline animation ── */
  useEffect(() => {
    const idx = TABS.findIndex(t => t.key === tab);
    const el  = tabEls.current[idx];
    if (!el || !inkRef.current) return;
    gsap.to(inkRef.current, {
      left: el.offsetLeft, width: el.offsetWidth,
      duration: 0.36, ease: "power2.inOut",
    });
  }, [tab]);

  /* ── Re-animate content on tab switch ── */
  useEffect(() => {
    if (!contentRef.current) return;
    gsap.fromTo(contentRef.current,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.36, ease: "power2.out" }
    );
    const els = contentRef.current.querySelectorAll(".gs");
    if (els.length) {
      gsap.fromTo(els,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, stagger: 0.07, duration: 0.4, ease: "power2.out", delay: 0.12 }
      );
    }
  }, [tab]);

  /* ── Resolve display name from API or Auth token ── */
  const displayName  = userInfo.name  || username || "—";
  const displayEmail = userInfo.email || "—";

  return (
    <>
      <div className="vp">
        <div className="container">

          {/* ════════ HERO ════════ */}
          <div ref={heroRef} className="vp-hero">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              {userInfo.avatar ? (
                <img src={userInfo.avatar} alt={displayName} className="vp-avatar" />
              ) : (
                <div className="vp-avatar-placeholder">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <p className="vp-hero-eyebrow">My Account</p>
                <h1 className="vp-hero-name">{displayName}</h1>
                <p className="vp-hero-email">{displayEmail}</p>
                {/* Dashboard greeting — same as Dashboard component */}
                <p className="vp-hero-desc">
                  Hello {username} — Today is a great day to check your account. You can view{" "}
                  <span
                    className="vp-hero-link"
                    onClick={() => setTab("orders")}
                  >
                    your orders
                  </span>{" "}
                  or{" "}
                  <span
                    className="vp-hero-link"
                    onClick={() => navigate("/shop")}
                  >
                    explore latest products
                  </span>.
                </p>

                {/* Latest order quick-view (Dashboard logic) */}
                {latestOrder && (
                  <div className="vp-latest-order">
                    <span className="vp-latest-order-label">Latest Order</span>
                    <span className="vp-latest-order-id">#{latestOrder.orderId}</span>
                    <span className="vp-latest-order-sep">·</span>
                    <span className="vp-latest-order-time">{getTimeAgo(latestOrder.createdAt)}</span>
                    <span className="vp-latest-order-sep">·</span>
                    <span className="vp-latest-order-amount">₹{latestOrder.total}/-</span>
                    <span
                      className="vp-latest-order-status"
                      style={{ color: STATUS_MAP[latestOrder.status]?.color || "var(--color-accent-main)" }}
                    >
                      <span
                        className="vp-order-dot"
                        style={{ background: STATUS_MAP[latestOrder.status]?.dot || "var(--color-accent-main)" }}
                      />
                      {latestOrder.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="vp-hero-rule" />
          </div>

          {/* ════════ TABS ════════ */}
          <div ref={tabsRef} className="vp-tabs-outer">
            <div className="vp-tabs-row">
              {TABS.map(({ key, label, Icon }, i) => (
                <button
                  key={key}
                  ref={(el) => (tabEls.current[i] = el)}
                  className={`vp-tab${tab === key ? " active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
            <span ref={inkRef} className="vp-ink" style={{ left: 0, width: 0 }} />
          </div>

          {/* ════════ CONTENT ════════ */}
          <div ref={contentRef} style={{ padding: "3rem 0 5rem" }}>

            {/* ── ORDERS ── */}
            {tab === "orders" && (
              <div>
                <p className="vp-eyebrow">Your Purchases</p>
                <h2 className="vp-title">Order History</h2>
                <div className="vp-rule" />

                {loadingOrders ? (
                  <OrderSkeleton />
                ) : orders.length === 0 ? (
                  <div className="vp-empty">
                    <ShoppingBag size={46} className="vp-empty-icon" />
                    <p className="vp-empty-title">No orders yet</p>
                    <p className="vp-empty-sub">You haven't placed any orders yet.</p>
                    <button className="vp-btn-primary" onClick={() => navigate("/shop")}>
                      Explore Collections <ArrowRight size={13} />
                    </button>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {orders.map((o) => (
                      <div key={o.orderId || o.id} className="vp-order gs">
                        {o.img || o.image ? (
                          <img src={o.img || o.image} alt={o.item || o.name} className="vp-order-img" />
                        ) : (
                          <div className="vp-order-img-placeholder">
                            <ShoppingBag size={20} />
                          </div>
                        )}
                        <div>
                          <p className="vp-order-id">#{o.orderId || o.id}</p>
                          <p className="vp-order-name">{o.item || o.name || o.productName}</p>
                          <p className="vp-order-date">{getTimeAgo(o.createdAt) || o.date}</p>
                          <span
                            className="vp-order-status"
                            style={{ color: STATUS_MAP[o.status]?.color || "var(--color-accent-main)" }}
                          >
                            <span className="vp-order-dot" style={{ background: STATUS_MAP[o.status]?.dot }} />
                            {o.status}
                          </span>
                        </div>
                        <div className="vp-order-right">
                          <p className="vp-order-amount">₹{o.total || o.amount}/-</p>
                          <button className="vp-btn-ghost" onClick={() => navigate(`/users/order-details/${o.orderId || o.id}`)}>
                            <Eye size={11} /> View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── WISHLIST ── */}
            {tab === "wishlist" && (
              <div>
                <p className="vp-eyebrow">Saved Pieces</p>
                <h2 className="vp-title">My Wishlist</h2>
                <div className="vp-rule" />

                {loadingWishlist ? (
                  <WishlistSkeleton />
                ) : wishlist.length === 0 ? (
                  <div className="vp-empty">
                    <Heart size={46} className="vp-empty-icon" />
                    <p className="vp-empty-title">Your wishlist is empty</p>
                    <p className="vp-empty-sub">Save pieces you love and find them here.</p>
                    <button className="vp-btn-primary" onClick={() => navigate("/shop")}>
                      Explore Collections <ArrowRight size={13} />
                    </button>
                  </div>
                ) : (
                  <div className="row g-4">
                    {wishlist.map((p) => (
                      <div key={p.id} className="col-6 col-md-3 gs">
                        <ProductCard product={{
                          image:    p.image    || p.thumbnail,
                          title:    p.title    || p.name,
                          category: p.category || p.categoryName,
                          price:    p.price    ? `₹${p.price}` : p.formattedPrice,
                          badge:    p.badge    || null,
                        }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── ADDRESSES ── */}
            {tab === "addresses" && (
              <div>
                <p className="vp-eyebrow">Shipping</p>
                <h2 className="vp-title">Saved Addresses</h2>
                <div className="vp-rule" />

                {loadingAddresses ? (
                  <div className="row g-3">
                    {[1, 2].map(i => (
                      <div key={i} className="col-md-4">
                        <div style={{ background: "var(--bg-tertiary)", padding: "1.5rem", borderRadius: 4 }}>
                          <div className="vp-skel medium" style={{ marginBottom: 10 }} />
                          <div className="vp-skel wide"   style={{ marginBottom: 8  }} />
                          <div className="vp-skel wide"   style={{ marginBottom: 8  }} />
                          <div className="vp-skel short"  />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="row g-3">
                    {addresses.map((a) => (
                      <div key={a.id} className="col-md-4 gs">
                        <div className={`vp-addr${a.default || a.isDefault ? " default" : ""}`}>
                          {(a.default || a.isDefault) && <span className="vp-addr-tag">Default</span>}
                          <p className="vp-addr-name">{a.name || a.fullName}</p>
                          <p className="vp-addr-text">
                            {a.line || a.address}<br />
                            {a.city}<br />
                            {a.pin || a.pincode || a.postalCode}
                          </p>
                          <p className="vp-addr-phone">{a.phone || a.phoneNumber}</p>
                          <div className="d-flex gap-2">
                            <button className="vp-btn-ghost" onClick={() => navigate("/users/addresses")}>
                              <Edit3 size={10} /> Edit
                            </button>
                            {!(a.default || a.isDefault) && (
                              <button className="vp-btn-ghost" onClick={() => handleDeleteAddress(a.id)}>
                                <Trash2 size={10} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="col-md-4 gs">
                      <button className="vp-add-card" onClick={() => navigate("/users/addresses")}>
                        <Plus size={22} style={{ color: "var(--color-accent-main)" }} />
                        <span className="vp-add-label">Add New Address</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── ACCOUNT DETAILS ── */}
            {tab === "account" && (
              <div>
                <p className="vp-eyebrow">Profile</p>
                <h2 className="vp-title">Account Details</h2>
                <div className="vp-rule" />

                {loadingAccount ? (
                  <div style={{ padding: "2rem 0" }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="vp-skel wide" style={{ marginBottom: 14, height: "2.5rem" }} />
                    ))}
                  </div>
                ) : (
                  <div className="row g-4 align-items-start">
                    <div className="col-lg-7">

                      {/* Personal Information */}
                      <div className="vp-block gs">
                        <p className="vp-block-title">Personal Information</p>
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="vp-label">Full Name</label>
                            <div className="vp-icon-wrap">
                              <User size={13} />
                              <input
                                className="vp-input"
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="vp-label">Email Address</label>
                            <div className="vp-icon-wrap">
                              <Mail size={13} />
                              <input
                                className="vp-input"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="vp-label">Phone Number</label>
                            <div className="vp-icon-wrap">
                              <Phone size={13} />
                              <input
                                className="vp-input"
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center mt-4">
                          <button className="vp-btn-primary" onClick={handleSave}>Save Changes</button>
                          {saved && <span className="vp-success"><Check size={13} /> Saved</span>}
                        </div>
                      </div>

                      {/* Change Password */}
                      <div className="vp-block gs">
                        <p className="vp-block-title">Change Password</p>
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="vp-label">Current Password</label>
                            <div className="vp-icon-wrap">
                              <Lock size={13} />
                              <input
                                className="vp-input"
                                type="password"
                                placeholder="••••••••"
                                value={form.curPwd}
                                onChange={(e) => setForm({ ...form, curPwd: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="vp-label">New Password</label>
                            <div className="vp-icon-wrap">
                              <Lock size={13} />
                              <input
                                className="vp-input"
                                type="password"
                                placeholder="••••••••"
                                value={form.newPwd}
                                onChange={(e) => setForm({ ...form, newPwd: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="vp-label">Confirm Password</label>
                            <div className="vp-icon-wrap">
                              <Lock size={13} />
                              <input
                                className="vp-input"
                                type="password"
                                placeholder="••••••••"
                                value={form.cfmPwd}
                                onChange={(e) => setForm({ ...form, cfmPwd: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="mt-4">
                          <button className="vp-btn-outline" onClick={handlePasswordUpdate}>
                            Update Password
                          </button>
                        </div>
                      </div>

                    </div>

                    {/* Side Panel */}
                    <div className="col-lg-4 offset-lg-1 gs">
                      <div className="vp-side-panel">
                        <p className="vp-side-panel-title">Account Summary</p>
                        {[
                          { Icon: ShoppingBag, label: "Total Orders",   val: orders.length   || userInfo.totalOrders   || "—" },
                          { Icon: Heart,       label: "Wishlist Items",  val: wishlist.length || userInfo.totalWishlist  || "—" },
                          { Icon: MapPin,      label: "Saved Addresses", val: addresses.length|| userInfo.totalAddresses || "—" },
                        ].map(({ Icon, label, val }) => (
                          <div key={label} className="vp-stat-row">
                            <span className="vp-stat-row-label">
                              <Icon size={13} style={{ color: "var(--color-accent-main)" }} />
                              {label}
                            </span>
                            <span className="vp-stat-row-val">{val}</span>
                          </div>
                        ))}
                      </div>

                      <div className="vp-danger-panel">
                        <p className="vp-danger-title">Danger Zone</p>
                        <p className="vp-danger-text">Permanently remove your account and all associated data from BEGUM.</p>
                        <button className="vp-danger-btn">Delete Account</button>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Skeleton CSS */}
      <style>{`
        .vp-skel {
          animation: vp-shimmer 1.6s infinite linear;
          background: linear-gradient(90deg, var(--bg-tertiary) 25%, var(--border-light) 50%, var(--bg-tertiary) 75%);
          background-size: 200% 100%;
          border-radius: 4px;
          height: 1rem;
        }
        .vp-skel.wide   { width: 100%; }
        .vp-skel.medium { width: 65%;  }
        .vp-skel.short  { width: 38%;  }
        @keyframes vp-shimmer {
          0%   { background-position: 200% 0;  }
          100% { background-position: -200% 0; }
        }
        .vp-avatar-placeholder {
          width: 90px; height: 90px;
          border-radius: 50%;
          background: var(--color-primary);
          color: var(--color-accent-main);
          font-family: var(--font-heading);
          font-size: 2rem;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .vp-hero-link {
          color: var(--color-accent-main);
          border-bottom: 1px solid rgba(184,150,90,0.4);
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .vp-hero-link:hover { border-color: var(--color-accent-main); }
        .vp-latest-order {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-light);
          padding: 0.4rem 0.85rem;
          font-family: var(--font-body);
          font-size: 0.78rem;
          flex-wrap: wrap;
        }
        .vp-latest-order-label {
          font-family: var(--font-accent);
          font-size: 0.62rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--color-accent-main);
        }
        .vp-latest-order-id     { color: var(--color-text-primary); font-weight: 500; }
        .vp-latest-order-sep    { color: var(--border-light); }
        .vp-latest-order-time   { color: var(--color-text-secondary); }
        .vp-latest-order-amount { color: var(--color-text-primary); font-weight: 500; }
        .vp-latest-order-status {
          display: inline-flex; align-items: center; gap: 4px;
          font-weight: 500;
        }
        .vp-order-img-placeholder {
          width: 80px; height: 90px;
          background: var(--bg-tertiary);
          display: flex; align-items: center; justify-content: center;
          color: var(--color-text-muted);
          flex-shrink: 0;
          border-radius: 4px;
        }
      `}</style>
    </>
  );
}